import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Save as SaveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { ClassService } from '../../services/classService';
import { Class, Student } from '../../types';

interface AttendanceRecord {
  studentId: string;
  present: boolean;
}

export function AttendanceMarking() {
  const [classes, setClasses] = useState<Class[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [confirmDialog, setConfirmDialog] = useState(false);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      setLoading(true);
      const result = await ClassService.getClasses(100);
      setClasses(result.classes);
    } catch (error) {
      console.error('Error loading classes:', error);
      showSnackbar('Failed to load classes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (classId: string) => {
    setSelectedClass(classId);
    const selectedClassData = classes.find(c => c.id === classId);
    if (selectedClassData) {
      // Initialize attendance records for all students
      const records = selectedClassData.students.map(student => ({
        studentId: student.id,
        present: false
      }));
      setAttendanceRecords(records);
    }
  };

  const handleAttendanceChange = (studentId: string, present: boolean) => {
    setAttendanceRecords(prev => 
      prev.map(record => 
        record.studentId === studentId 
          ? { ...record, present }
          : record
      )
    );
  };

  const handleMarkAllPresent = () => {
    setAttendanceRecords(prev => prev.map(record => ({ ...record, present: true })));
  };

  const handleMarkAllAbsent = () => {
    setAttendanceRecords(prev => prev.map(record => ({ ...record, present: false })));
  };

  const handleSaveAttendance = () => {
    setConfirmDialog(true);
  };

  const confirmSaveAttendance = async () => {
    try {
      setSaving(true);
      setConfirmDialog(false);
      
      const selectedClassData = classes.find(c => c.id === selectedClass);
      if (!selectedClassData) {
        throw new Error('Selected class not found');
      }

      // Call ClassService to save attendance
      await ClassService.saveAttendance(selectedClass, selectedDate, attendanceRecords);
      
      showSnackbar('Attendance saved successfully', 'success');
      
      // Reset form
      setSelectedClass('');
      setAttendanceRecords([]);
      setSelectedDate(new Date().toISOString().split('T')[0]);
      
    } catch (error) {
      console.error('Error saving attendance:', error);
      showSnackbar('Failed to save attendance', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const selectedClassData = classes.find(c => c.id === selectedClass);
  const presentCount = attendanceRecords.filter(r => r.present).length;
  const totalCount = attendanceRecords.length;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Mark Attendance
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TodayIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Today</Typography>
                  <Typography color="textSecondary">
                    {new Date().toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Present</Typography>
                  <Typography color="textSecondary">
                    {presentCount} / {totalCount}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h6">Attendance Rate</Typography>
                  <Typography color="textSecondary">
                    {totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* QR Code Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Mobile App for Attendance
        </Typography>
        <Grid container spacing={4} alignItems="stretch">
          <Grid item xs={12} lg={5}>
            <Box sx={{ 
              textAlign: 'center', 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              p: 2
            }}>
              <img 
                src="/appqr.jpg" 
                alt="QR Code for App Download"
                style={{ 
                  maxWidth: '220px', 
                  width: '100%',
                  height: 'auto',
                  border: '2px solid #ddd',
                  borderRadius: '12px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  margin: '0 auto'
                }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontWeight: 500 }}>
                Scan QR code to download app
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} lg={7}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Download Mobile App
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.6 }}>
                Use our mobile app for quick attendance marking on the go. The app includes:
              </Typography>
              <Box component="ul" sx={{ 
                pl: 3, 
                mb: 3, 
                '& li': { mb: 0.5, fontSize: '14px', lineHeight: 1.5 } 
              }}>
                <li>Quick login functionality</li>
                <li>Fast attendance marking</li>
                <li>Offline capability</li>
                <li>Easy to use interface</li>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="https://www.mediafire.com/file/e093lheqgccdbi5/base.apk/file"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ minWidth: 'fit-content' }}
                >
                  Download APK
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="/app-download"
                  sx={{ minWidth: 'fit-content' }}
                >
                  Installation Guide
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {/* Selection Controls */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select Class</InputLabel>
              <Select
                value={selectedClass}
                onChange={(e) => handleClassChange(e.target.value)}
                label="Select Class"
                disabled={loading}
              >
                {classes.map((classItem) => (
                  <MenuItem key={classItem.id} value={classItem.id}>
                    {classItem.name} ({classItem.students.length} students)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel></InputLabel>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '16px 14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={handleMarkAllPresent}
                disabled={!selectedClass}
                size="small"
              >
                All Present
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleMarkAllAbsent}
                disabled={!selectedClass}
                size="small"
              >
                All Absent
              </Button>
            </Box>
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : selectedClassData ? (
          <>
            {/* Class Info */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="h6">{selectedClassData.name}</Typography>
              <Typography>
                Total Students: {selectedClassData.students.length} | 
                Date: {new Date(selectedDate).toLocaleDateString()} |
                Present: {presentCount} | 
                Absent: {totalCount - presentCount}
              </Typography>
            </Alert>

            {/* Student List */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student Name</TableCell>
                    <TableCell>Roll Number</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Mark Attendance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedClassData.students.map((student) => {
                    const record = attendanceRecords.find(r => r.studentId === student.id);
                    const isPresent = record?.present || false;
                    
                    return (
                      <TableRow key={student.id}>
                        <TableCell>{student.name}</TableCell>
                        <TableCell>{student.id}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={isPresent ? 'Present' : 'Absent'}
                            color={isPresent ? 'success' : 'error'}
                            icon={isPresent ? <CheckIcon /> : <CloseIcon />}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={isPresent}
                            onChange={(e) => handleAttendanceChange(student.id, e.target.checked)}
                            color="success"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSaveAttendance}
                disabled={saving || totalCount === 0}
              >
                {saving ? 'Saving...' : 'Save Attendance'}
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography color="textSecondary">
              Select a class to mark attendance
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Attendance</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to save attendance for {selectedClassData?.name} on {new Date(selectedDate).toLocaleDateString()}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button onClick={confirmSaveAttendance} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 